import { faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useMemo } from 'react';

import { BUSINESS_SOLUTION_ORGANIZATION_ID } from '@app/access/constants/organizations';
import { config } from '@app/config';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';

import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';

type LeadCardAddActionId =
  | 'tags'
  | 'comment'
  | 'file'
  | 'record'
  | 'task'
  | 'organization_comment';

export interface LeadCardAddActionItem {
  id: LeadCardAddActionId;
  icon: IconDefinition;
  label: string;
  isFullWidth?: boolean;
  onClick(): void;
}

interface UseLeadCardAddActionsParams {
  openTagsModal(): void;
  openCommentModal(): void;
  openFileModal(): void;
  openBranchSelectionModal(): void;
  openTaskModal(): void;
  handleOrganizationCommentClick(): void;
  isFileAvailable: boolean;
}

export const useAddActions = ({
  openTagsModal,
  openCommentModal,
  openFileModal,
  openBranchSelectionModal,
  openTaskModal,
  handleOrganizationCommentClick,
  isFileAvailable,
}: UseLeadCardAddActionsParams) => {
  const {
    userAccessRights: {
      Clients: { IsTagsChange },
      Records: { IsCreate: isRecordCreate },
      CrmTasks: { IsCreate: isCrmTasksCreate },
    },
  } = useNewUserAccessRights();

  const { database } = useUserPrint();

  //TODO - переделать логику отображения комментария к организации, так как сейчас она завязана на конкретную организацию и режим работы приложения. Нужно либо вынести эту логику в отдельный хук, либо сделать более универсальную проверку прав доступа, которая будет учитывать возможность комментирования организации в целом, а не только для конкретной организации и режима работы.
  const isOrganizationCommentVisible =
    config.mode === 'production'
      ? database === BUSINESS_SOLUTION_ORGANIZATION_ID
      : true;

  const addActionItems = useMemo<LeadCardAddActionItem[]>(() => {
    const items = [
      {
        id: 'tags',
        label: 'Теги',
        icon: faEdit,
        isShown: IsTagsChange,
        onClick: openTagsModal,
      },

      {
        id: 'record',
        label: 'Запись',
        icon: faPlus,
        isShown: isRecordCreate,
        onClick: openBranchSelectionModal,
      },
      {
        id: 'task',
        label: 'Задача',
        icon: faPlus,
        isShown: isCrmTasksCreate,
        onClick: openTaskModal,
      },
      {
        id: 'comment',
        label: 'Комментарий',
        icon: faPlus,
        isShown: true,
        onClick: openCommentModal,
      },
      {
        id: 'file',
        label: 'Файл или фото',
        icon: faPlus,
        isShown: isFileAvailable,
        onClick: openFileModal,
      },
      {
        id: 'organization_comment',
        label: 'Комментарий к организации',
        onClick: handleOrganizationCommentClick,
        icon: faPlus,
        isShown: isOrganizationCommentVisible,
      },
    ] as const;

    const visibleItems = items.reduce<LeadCardAddActionItem[]>(
      (result, { isShown, ...rest }) => {
        if (!isShown) return result;

        result.push({
          ...rest,
        });

        return result;
      },
      []
    );

    const organizationCommentItem = visibleItems.find(
      ({ id }) => id === 'organization_comment'
    );
    const regularItems = visibleItems.filter(
      ({ id }) => id !== 'organization_comment'
    );
    const regularItemsLastIndex = regularItems.length - 1;
    const shouldStretchLastRegularItem = regularItems.length % 2 === 1;

    return [
      ...regularItems.map((item, index) => ({
        ...item,
        isFullWidth:
          shouldStretchLastRegularItem && index === regularItemsLastIndex,
      })),
      ...(organizationCommentItem
        ? [{ ...organizationCommentItem, isFullWidth: true }]
        : []),
    ];
  }, [
    IsTagsChange,
    openTagsModal,
    openCommentModal,
    isFileAvailable,
    openFileModal,
    isRecordCreate,
    openBranchSelectionModal,
    isCrmTasksCreate,
    openTaskModal,
    handleOrganizationCommentClick,
    isOrganizationCommentVisible,
  ]);

  return useMemo(
    () => ({
      addActionItems,
    }),
    [addActionItems]
  );
};
